import { isLifecycleInFlight, type AgentRepoStatus, type DevcontainerStatus } from '../stores/agent_repos'
import { pollIntervalLabel } from '../composables/pollIntervalLabel'

// At-a-glance labels for one row of the Repos table. Every state carries a
// text label as well as a dot colour, so colour is never the only signal:
// mint = up and healthy, amber = work in progress (the factory colour),
// muted grey = idle, faint grey = stopped / absent.
export interface RepoStateBadge {
  label: string
  dotClass: string
  textClass: string
  // Tinted pill for the detail panel's section header.
  pillClass: string
}

const NEUTRAL_PILL = 'bg-surface-hover text-ink-muted'
const FACTORY_PILL = 'bg-factory/15 text-factory'

const STATUS_BADGES: Record<DevcontainerStatus, RepoStateBadge> = {
  running: {
    label: 'Running',
    dotClass: 'bg-brand-primary',
    textClass: 'text-ink',
    pillClass: 'bg-brand-primary/15 text-brand-primary',
  },
  idle: { label: 'Idle', dotClass: 'bg-ink-muted', textClass: 'text-ink-muted', pillClass: NEUTRAL_PILL },
  stopped: { label: 'Stopped', dotClass: 'bg-ink-faint/60', textClass: 'text-ink-faint', pillClass: NEUTRAL_PILL },
}

// A status the table doesn't know yet (a newer server, a missing field)
// renders as a neutral label instead of breaking the whole page.
function statusBadge(status: string | null | undefined): RepoStateBadge {
  const known = status ? STATUS_BADGES[status as DevcontainerStatus] : undefined
  return (
    known ?? {
      label: status || 'Unknown',
      dotClass: 'bg-ink-faint/60',
      textClass: 'text-ink-faint',
      pillClass: NEUTRAL_PILL,
    }
  )
}

// The devcontainer column: an in-flight lifecycle action (#1051) outranks the
// container's own status, since that status is about to change.
export function repoContainerBadge(repo: AgentRepoStatus): RepoStateBadge {
  if (isLifecycleInFlight(repo)) {
    return {
      label: `${repo.lifecycle_action ?? 'Action'} ${repo.lifecycle_status}`,
      dotClass: 'bg-factory animate-pulse',
      textClass: 'text-factory',
      pillClass: FACTORY_PILL,
    }
  }
  if (!repo.devcontainer_exists) {
    return {
      label: 'No devcontainer',
      dotClass: 'bg-ink-faint/60',
      textClass: 'text-ink-faint',
      pillClass: NEUTRAL_PILL,
    }
  }
  return statusBadge(repo.container_status)
}

// The agent-session column: an isolated session (#1352) runs in a global slot,
// not the repo's own container, so it has a status of its own. Running here is
// agent work, so it takes the factory amber rather than the healthy mint.
export function repoSessionBadge(repo: AgentRepoStatus): RepoStateBadge {
  if (repo.agent_session_status === 'running') {
    return {
      label: 'Running',
      dotClass: 'bg-factory animate-pulse',
      textClass: 'text-factory',
      pillClass: FACTORY_PILL,
    }
  }
  return statusBadge(repo.agent_session_status)
}

// The product-owner column: off, or on with its computed check-in cadence.
export function repoProductOwnerLabel(repo: AgentRepoStatus): string {
  if (!repo.product_owner_enabled) return 'Off'
  return `Every ${pollIntervalLabel(repo.product_owner_poll_interval_minutes)}`
}
